import { type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import type { PropertyFileTreeNode } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { AkteBrowserTreeNode } from './AkteBrowserTreeNode';
import { findNodeByPath } from './akteBrowserNav';
import { TOP_FOLDERS } from './akteFolderMeta';
import type { useAkteDragMove } from './useAkteDragMove';

interface Props {
  tree: PropertyFileTreeNode;
  currentPath: string;
  trashCount: number;
  trashActive: boolean;
  onNavigate: (path: string) => void;
  onOpenTrash: () => void;
  /** Kontextmenü-Renderer für Custom-Ordner — von C2 injiziert. */
  renderMenu?: (node: PropertyFileTreeNode) => ReactNode;
  /** Drop-Ziel-Handler (Drag-Move auf einen Sidebar-Ordner). */
  drop?: ReturnType<typeof useAkteDragMove>['dropProps'];
  /** Drop-Ziel für den Papierkorb (Drag = Soft-Delete + Undo). */
  trashDrop?: ReturnType<ReturnType<typeof useAkteDragMove>['trashDropProps']>;
}

/** Persistente Ordner-Baum-Sidebar (links). Kategorien 01–99 immer sichtbar + Papierkorb-Eintrag. */
export function AkteBrowserSidebar({
  tree,
  currentPath,
  trashCount,
  trashActive,
  onNavigate,
  onOpenTrash,
  renderMenu,
  drop,
  trashDrop,
}: Props) {
  return (
    <nav aria-label="Ordner" className="flex flex-col gap-0.5">
      {/* Mobil steht „Ordner" schon auf dem Disclosure-Auslöser — hier wäre es die zweite Kopie. */}
      <p className="text-ink-faint mb-1.5 hidden px-1 text-xs font-semibold uppercase tracking-wider lg:block">
        Ordner
      </p>

      {TOP_FOLDERS.map((meta) => {
        const node = findNodeByPath(tree, meta.folderPath);
        if (!node) return null;
        return (
          <AkteBrowserTreeNode
            key={meta.folderPath}
            node={node}
            icon={meta.icon}
            title={meta.title}
            depth={0}
            currentPath={currentPath}
            onNavigate={onNavigate}
            renderMenu={renderMenu}
            drop={drop}
          />
        );
      })}

      <button
        type="button"
        onClick={onOpenTrash}
        {...trashDrop?.props}
        className={cn(
          'mt-3 flex items-center gap-2.5 rounded-md border-l-[3px] px-2.5 py-2.5 text-left text-[15px] transition-colors',
          trashDrop?.active
            ? 'bg-sealwax/15 border-sealwax text-sealwax ring-sealwax/40 ring-1'
            : trashActive
              ? 'bg-sealwax/10 border-sealwax text-sealwax'
              : 'hover:bg-card-alt/60 text-ink border-transparent',
        )}
      >
        <Trash2 size={17} className="shrink-0 opacity-80" />
        <span className="flex-1 font-serif font-medium">Papierkorb</span>
        {trashCount > 0 && (
          <span className="bg-card text-ink-soft rounded px-1.5 py-0.5 text-xs tabular-nums">
            {trashCount}
          </span>
        )}
      </button>
    </nav>
  );
}
